import React, { useContext, useEffect, useMemo, useState } from "react";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { useProductDetailContext } from "@contexts/product-detail-context";

import ThemeBoxDetails from "@components/store/product-detail/theme-box/theme-box-details";
import ProductCarousel from "@components/store/product-detail/shared/product-detail-carousel";
import ProductDetailHeader from "@components/store/product-detail/shared/product-detail-header";

import { useProductInfoContext } from "@contexts/product-info-context";
import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

import { DEFAULT_QUANTITY } from "@constants/products/detail";

import styles from "./main.module.scss";

const ThemeBox = ({ product, masterVariant, canBuyProduct }) => {
  const { modalClasses, hidePriceDetail } = useProductInfoContext();

  const { productImgClass = "", productContentClass = "" } = modalClasses;

  const [productQuantity, setProductQuantity] = useState(DEFAULT_QUANTITY);
  const [variantSelected, setSelectedVariant] = useState({});
  const priceOptions = product.variantsIncludingMaster;
  const {
    editProductIds: [editProductId],
  } = useProductDetailContext();
  const { formatPriceInDisplayStoreCurrency } = useStoreCurrencyConverter();
  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const medleyProductOptions = useMemo(() => {
    return priceOptions.map((variant) => ({
      ...variant,
      amountIn: formatPriceInDisplayStoreCurrency(variant.priceIn),
    }));
  }, [priceOptions, formatPriceInDisplayStoreCurrency]);

  const getDisplayPrice = () => {
    const medleyProductOptionsLength = medleyProductOptions.length;
    if (medleyProductOptionsLength > 1) {
      const minPrice = medleyProductOptions[0].amountIn;
      const maxPrice = medleyProductOptions[medleyProductOptionsLength - 1].amountIn;
      return `${minPrice} - ${maxPrice}`;
    }
    return masterVariant.amountIn || product.amountIn;
  };

  const sliderImages = useMemo(() => {
    if (product.variantsIncludingMaster.every(({ images }) => images.length === 0))
      return [{ id: "image0", image: product.variantImages[0] }];
    return product.variantsIncludingMaster.map((variant, index) => {
      return {
        id: `image${index}`,
        image: variant?.images?.[0] || product.variantImages[0],
      };
    });
  }, [product]);

  const selectedImageIndex = useMemo(() => {
    return priceOptions.findIndex((opt) => opt.id === variantSelected?.id);
  }, [variantSelected]);

  const onChangeSliderImage = (index) => {
    setSelectedVariant(priceOptions[index]);
  };

  //reset product quantity on variant change
  useEffect(() => {
    !editProductId && setProductQuantity(DEFAULT_QUANTITY);
  }, [variantSelected, editProductId]);

  const renderProductDetailHeader = () => (
    <ProductDetailHeader
      displayPrice={!hidePriceDetail && getDisplayPrice()}
      product={product}
      showProductName
    />
  );

  const renderProductDetails = () => (
    <ThemeBoxDetails
      product={product}
      masterVariant={masterVariant}
      productQuantity={productQuantity}
      setProductQuantity={setProductQuantity}
      variantSelected={variantSelected}
      setSelectedVariant={setSelectedVariant}
      canBuyProduct={canBuyProduct}
      renderProductDetailHeader={renderProductDetailHeader}
      medleyProductOptions={medleyProductOptions}
    />
  );

  return (
    <div>
      <div
        data-type="product-row"
        className={`${styles.rowProductDetail} themeBox ${styles.rowWithV2Enabled}`}
      >
        {isMobileDevice && renderProductDetailHeader()}
        <div className={`${styles.productCarousalWrapper} ${productImgClass}`}>
          <ProductCarousel
            sliderImages={sliderImages}
            selectedImageIndex={selectedImageIndex}
            setSelectedImageIndex={onChangeSliderImage}
            productId={product.id}
            product={product}
            imageMagnify
            imageCoverRequired
            carouselContainerClass={styles.withVerticalSlider}
            thumbnailContainterClass={styles.thumbBlock}
          />
        </div>
        <div className={`${styles.prdCol} ${productContentClass}`}>{renderProductDetails()}</div>
      </div>
    </div>
  );
};

export default ThemeBox;
